import { Business } from '../types';

// API Service for connecting to backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

// Helper function for API calls
async function apiCall<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const response = await fetch(url, { ...defaultOptions, ...options });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// Business API
export const paymentAPI = {
  // Create a new business
  createCheckoutSession: async (planId: string, org: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/payment/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId, org }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create checkout session');
      }

      const data = await response.json();
      return data.clientSecret;
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  },

  cancelSubscription: async (subscriptionId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/payment/cancel-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscriptionId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to cancel subscription');
      }
      return response.json();
    } catch (error: any) {
      console.error('Error cancel subscription:', error);
      throw error;
    }

  },

  renewSubscription: async (subscriptionId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/payment/renew-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscriptionId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to renew subscription');
      }
      return response.json();
    } catch (error: any) {
      console.error('Error renew subscription:', error);
      throw error;
    }

  },

  upgradeSubscription: async (planId: string, subscriptionId: string) => {
    const res = await fetch(`${API_BASE_URL}/payment/upgrade-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ planId, subscriptionId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to update plan');
    }
    return res.json();
  },
  // Get all membership plans (public - no auth required)
  getMembershipPlan: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/payment/getMembershipPlan`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch membership plans');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching membership plans:', error);
      throw error;
    }
  },

}

// Business API
export const businessAPI = {
  // Create a new business
  create: async (businessData: Partial<Business>) => {
    try {
      const response = await fetch(`${API_BASE_URL}/businesses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(businessData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create business');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error creating business:', error);
      throw error;
    }
  },

  // Update a business infomation
  update: async (id: string, businessData: Partial<Business>) => {
    const response = await fetch(`${API_BASE_URL}/businesses/${id}`, {
      method: 'PUT', // or 'PATCH' if partial updates
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(businessData),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to update business');
    }
    return response.json();
  },

  // Delete a business
  delete: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/businesses/${id}`, {
      method: 'DELETE', // or 'PATCH' if partial updates
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to update business');
    }
    return response.json();
  },

  // Get all businesses
  getAll: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/businesses`);
      if (!response.ok) {
        // If endpoint doesn't exist yet, return empty array
        console.warn('Business API endpoint not available yet, using fallback');
        return [];
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching businesses:', error);
      return [];
    }
  },

  // Get all businesses
  getAllPositive: async (minRating: number | null = null, latitude?: number, longitude?: number, radius?: number, pagination?: { page?: number; limit?: number }) => {
    try {
      const params = new URLSearchParams();

      if (minRating !== null) {
        params.append('minRating', minRating.toString());
      }

      if (latitude !== undefined && longitude !== undefined) {
        params.append('latitude', latitude.toString());
        params.append('longitude', longitude.toString());
        if (radius !== undefined) params.append('radius', radius.toString());
      }

      if (pagination) {
        if (pagination.page) params.append('page', pagination.page.toString());
        if (pagination.limit) params.append('limit', pagination.limit.toString());
      }

      const response = await fetch(`${API_BASE_URL}/businesses/positive?${params.toString()}`);
      if (!response.ok) {
        // If endpoint doesn't exist yet, return empty array
        console.warn('Business API endpoint not available yet, using fallback');
        return [];
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching businesses:', error);
      return [];
    }
  },

  // Get business by ID
  getById: async (id: string) => {
    try {
      return await apiCall(`/businesses/${id}`);
    } catch (error) {
      console.error(`Error fetching business ${id}:`, error);
      return null;
    }
  },

  // Search businesses
  search: async (filters: any) => {
    try {
      const params = new URLSearchParams(filters);
      return await apiCall(`/businesses/search?${params}`);
    } catch (error) {
      console.error('Error searching businesses:', error);
      return [];
    }
  },

  // Get nearby businesses
  getNearby: async (lat: number, lng: number, radius: number = 10) => {
    try {
      return await apiCall(`/businesses/nearby?latitude=${lat}&longitude=${lng}&radius=${radius}`);
    } catch (error) {
      console.error('Error fetching nearby businesses:', error);
      return [];
    }
  },

  // increment business view count
  incrementViewCount: async (userId: string, businessId: string) => {
    const response = await fetch(`${API_BASE_URL}/businesses/view/${userId}/${businessId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, businessId }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to increment view count');
    }
    return response.json();
  },

};

// Review API
export const reviewAPI = {
  // Get reviews for a business
  getByBusiness: async (businessId: string, minRating?: number, page: number = 1, limit: number = 20) => {
    try {
      const response = await fetch(`${API_BASE_URL}/reviews/business/${businessId}?page=${page}&limit=${limit}${minRating ? `&minRating=${minRating}` : ''}`);
      if (!response.ok) {
        console.warn('Failed to fetch reviews');
        return {
          data: [],
          pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
          averageRating: 0,
          totalFilteredReviews: 0
        };
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching reviews for business ${businessId}:`, error);
      return {
        data: [],
        pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
        averageRating: 0,
        totalReviews: 0
      };
    }
  },

  // Get reviews for a business
  getByOrg: async (orgId: string, page: number = 1, limit: number = 20) => {
    try {
      const response = await fetch(`${API_BASE_URL}/reviews/organization/${orgId}?page=${page}&limit=${limit}`);
      if (!response.ok) {
        console.warn('Failed to fetch reviews');
        return { data: [], pagination: { page: 1, limit: 20, total: 0, totalPages: 0 } };
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching reviews for organization ${orgId}:`, error);
      return { data: [], pagination: { page: 1, limit: 20, total: 0, totalPages: 0 } };
    }
  },

  // Get reviews for a business
  summaryByOrg: async (orgId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/reviews/organization/${orgId}/summary`);
      if (!response.ok) {
        console.warn('Failed to fetch reviews');
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching reviews for organization ${orgId}:`, error);
      return null;
    }
  },

  // Create review
  create: async (reviewData: {
    businessId: string;
    userId: string;
    title: string;
    content: string;
    rating: number;
    aspects?: Array<{ category: string; rating: number; comment?: string }>;
    photos?: string[];
    isAnonymous?: boolean;
  }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create review');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error creating review:', error);
      throw error;
    }
  },

  // Add helpful vote
  addHelpfulVote: async (reviewId: string, userId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/reviews/${reviewId}/helpful`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add helpful vote');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error adding helpful vote:', error);
      throw error;
    }
  },

  // Get review stats for a business
  getStats: async (businessId: string, from?: string, to?: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/reviews/business/${businessId}/stats-range`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from, to }),
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Error fetching review stats:', error);
      return null;
    }
  },

  // Get review stats for a business
  getOrgStats: async (orgId: string, from?: string, to?: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/reviews/organization/${orgId}/stats-range`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from, to }),
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Error fetching review stats:', error);
      return null;
    }
  },

  getWithAspects: async (reviewId: string) => {
    const response = await fetch(`${API_BASE_URL}/reviews/${reviewId}`);
    if (!response.ok) throw new Error('Failed to fetch review');
    return response.json();
  },

  addBusinessResponse: async (reviewId: string, responseText: string) => {
    const res = await fetch(`${API_BASE_URL}/reviews/${reviewId}/response`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ response: responseText }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to save response');
    }
    return res.json();
  },
};

// User API
export const userAPI = {
  // Register user
  register: async (userData: { firstName: string; lastName: string; email: string; password: string; type: string }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Registration failed');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error registering user:', error);
      throw error;
    }
  },

  // Login user
  login: async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error logging in:', error);
      throw error;
    }
  },

  // Get user profile (requires auth token)
  getProfile: async (token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      if (!response.ok) throw new Error('Failed to get profile');
      return await response.json();
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  },


  // Send / resend an OTP (email verification today; phone later)
  sendOtp: async (
    token: string,
    opts: { channel?: 'email' | 'phone'; purpose?: 'email_verify' | 'phone_verify'; destination?: string } = {}
  ) => {
    const response = await fetch(`${API_BASE_URL}/auth/otp/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        channel: opts.channel || 'email',
        purpose: opts.purpose || 'email_verify',
        destination: opts.destination,
      }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to send verification code');
    }
    return response.json();
  },

  // Verify an OTP code for the authenticated account
  verifyOtp: async (
    token: string,
    opts: { code: string; channel?: 'email' | 'phone'; purpose?: 'email_verify' | 'phone_verify' }
  ) => {
    const response = await fetch(`${API_BASE_URL}/auth/otp/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        code: opts.code,
        channel: opts.channel || 'email',
        purpose: opts.purpose || 'email_verify',
      }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to verify code');
    }
    return response.json();
  },

  // Forgot password — request a reset email
  forgotPassword: async (email: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to request password reset');
    }
    return response.json();
  },

  // Validate a reset token before showing the new-password form
  validateResetToken: async (token: string) => {
    const response = await fetch(
      `${API_BASE_URL}/auth/reset-password/validate?token=${encodeURIComponent(token)}`
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to validate reset token');
    }
    return response.json() as Promise<{ valid: boolean; error?: string }>;
  },

  // Reset password with a token from the email link
  resetPassword: async (token: string, password: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to reset password');
    }
    return response.json();
  },

  // user interests
  getInterests: async (userId: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/user/interests?userId=${userId}`);
      if (!res.ok) throw new Error('Failed to get Interests');
      return await res.json();
    } catch (error) {
      console.error(`Error fetching interests :`, error);
      throw error;
    }

  },
  saveInterests: async ({ userId, interests }: { userId: string; interests: any }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/interests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, interests }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to save interests');
      }
      return response.json();
    }
    catch (error) {
      console.error('Error adding user interests:', error);
      throw error;
    }
  },
  updatePreferences: async ({ userId, preferences }: { userId: string; preferences: any }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, preferences }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to save preferences');
      }
      return response.json();
    }
    catch (error) {
      console.error('Error adding user preferences:', error);
      throw error;
    }
  },
};


// Organization API
export const orgAPI = {
  // Register org
  register: async (orgData: any) => {
    try {
      const response = await fetch(`${API_BASE_URL}/business-auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orgData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Business registration failed');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error registering business:', error);
      throw error;
    }
  },

  // Login business (uses the unified auth/login endpoint)
  login: async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Business login failed');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error logging in business:', error);
      throw error;
    }
  },

  // Get business profile (requires auth token)
  getProfile: async (token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/business-auth/profile`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      if (!response.ok) throw new Error('Failed to get business profile');
      return await response.json();
    } catch (error) {
      console.error('Error fetching business profile:', error);
      return null;
    }
  },

  // Update org profile (general fields)
  updateProfile: async (updates: any, token: string) => {
    const res = await fetch(`${API_BASE_URL}/business-auth/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updates),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to update profile');
    }
    return res.json();
  },

  // Get all membership plans (public - no auth required)
  getMembershipPlans: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/business-auth/membership-plans`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch membership plans');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching membership plans:', error);
      throw error;
    }
  },

  // Get membership plan by ID (public - no auth required)
  getMembershipPlanById: async (planId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/business-auth/membership-plans/${planId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch membership plan');
      }

      return await response.json();
    } catch (error: any) {
      console.error(`Error fetching membership plan ${planId}:`, error);
      throw error;
    }
  },

  updateMembershipPlan: async (planId: string, token: string) => {
    const res = await fetch(`${API_BASE_URL}/business-auth/membership-plan`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ membershipPlanId: planId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to update plan');
    }
    return res.json();
  },
};

// // Business Member API
// export const businessMemberAPI = {
//   // Register business
//   register: async (businessData: any) => {
//     try {
//       const response = await fetch(`${API_BASE_URL}/business-auth/register`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify(businessData),
//       });

//       if (!response.ok) {
//         const error = await response.json();
//         throw new Error(error.error || 'Business registration failed');
//       }

//       return await response.json();
//     } catch (error: any) {
//       console.error('Error registering business:', error);
//       throw error;
//     }
//   },

//   // Login business
//   login: async (email: string, password: string) => {
//     try {
//       const response = await fetch(`${API_BASE_URL}/business-auth/login`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({ email, password }),
//       });

//       if (!response.ok) {
//         const error = await response.json();
//         throw new Error(error.error || 'Business login failed');
//       }

//       return await response.json();
//     } catch (error: any) {
//       console.error('Error logging in business:', error);
//       throw error;
//     }
//   },

//   // Get business profile (requires auth token)
//   getProfile: async (token: string) => {
//     try {
//       const response = await fetch(`${API_BASE_URL}/business-auth/profile`, {
//         headers: {
//           'Content-Type': 'application/json',
//           'Authorization': `Bearer ${token}`
//         },
//       });

//       if (!response.ok) throw new Error('Failed to get business profile');
//       return await response.json();
//     } catch (error) {
//       console.error('Error fetching business profile:', error);
//       return null;
//     }
//   },

//   // Get all membership plans (public - no auth required)
//   getMembershipPlans: async () => {
//     try {
//       const response = await fetch(`${API_BASE_URL}/business-auth/membership-plans`, {
//         method: 'GET',
//         headers: { 'Content-Type': 'application/json' },
//       });

//       if (!response.ok) {
//         const error = await response.json();
//         throw new Error(error.error || 'Failed to fetch membership plans');
//       }

//       return await response.json();
//     } catch (error: any) {
//       console.error('Error fetching membership plans:', error);
//       throw error;
//     }
//   },

//   // Get membership plan by ID (public - no auth required)
//   getMembershipPlanById: async (planId: string) => {
//     try {
//       const response = await fetch(`${API_BASE_URL}/business-auth/membership-plans/${planId}`, {
//         method: 'GET',
//         headers: { 'Content-Type': 'application/json' },
//       });

//       if (!response.ok) {
//         const error = await response.json();
//         throw new Error(error.error || 'Failed to fetch membership plan');
//       }

//       return await response.json();
//     } catch (error: any) {
//       console.error(`Error fetching membership plan ${planId}:`, error);
//       throw error;
//     }
//   },
// };

// Bookmark API
export const bookmarkAPI = {
  // Get user bookmarks
  getByUser: async (userId: string, page: number = 1, limit: number = 20) => {
    try {
      return await apiCall(`/bookmarks/user/${userId}?page=${page}&limit=${limit}`);
    } catch (error) {
      console.error(`Error fetching bookmarks for user ${userId}:`, error);
      return { data: [], pagination: { page: 1, limit: 20, total: 0, totalPages: 0 } };
    }
  },

  // Add bookmark
  add: async (userId: string, businessId: string, note?: string, tags?: string[]) => {
    try {
      return await apiCall('/bookmarks', {
        method: 'POST',
        body: JSON.stringify({ userId, businessId, note, tags }),
      });
    } catch (error) {
      console.error('Error adding bookmark:', error);
      throw error;
    }
  },

  // Remove bookmark
  remove: async (userId: string, businessId: string) => {
    try {
      return await apiCall(`/bookmarks`, {
        method: 'DELETE',
        body: JSON.stringify({ userId, businessId }),
      });
    } catch (error) {
      console.error('Error removing bookmark:', error);
      throw error;
    }
  },

  getByBusiness: async (businessId: string, page: number = 1, limit: number = 20) => {
    try {
      return await apiCall(`/bookmarks/business/${businessId}?page=${page}&limit=${limit}`);
    } catch (error) {
      console.error(`Error fetching bookmarks for business ${businessId}:`, error);
      return { data: [], pagination: { page: 1, limit: 20, total: 0, totalPages: 0 } };
    }
  },

  getByOrg: async (orgId: string, page: number = 1, limit: number = 20) => {
    try {
      return await apiCall(`/bookmarks/organization/${orgId}?page=${page}&limit=${limit}`);
    } catch (error) {
      console.error(`Error fetching bookmarks for organization ${orgId}:`, error);
      return { data: [], pagination: { page: 1, limit: 20, total: 0, totalPages: 0 } };
    }
  },
};

// Test connection
export const testConnection = async (): Promise<boolean> => {
  try {
    // Use the API base URL but remove /api/v1 and add /health
    const healthUrl = API_BASE_URL.replace('/api/v1', '') + '/health';
    const response = await fetch(healthUrl);
    if (response.ok) {
      const data = await response.json();
      console.log('✅ Backend connection successful:', data);
      return true;
    }
    return false;
  } catch (error) {
    console.warn('⚠️ Backend not available, using fallback mode');
    return false;
  }
};

// S3 Upload API
export const uploadAPI = {
  getReviewPhotoUploadUrl: async (payload: { fileName: string; fileType: string; reviewId?: string }) => {
    const response = await fetch(`${API_BASE_URL}/uploads/review-photo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include', // if you rely on cookies
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to get upload URL');
    }
    return response.json(); // { uploadUrl, fileUrl, key }
  },
};



export default {
  business: businessAPI,
  review: reviewAPI,
  user: userAPI,
  // businessMember: businessMemberAPI,
  bookmark: bookmarkAPI,
  upload: uploadAPI,
  org: orgAPI,
  testConnection,
};

